import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CardDetails {
  number: string;
  exp_month: number;
  exp_year: number;
  cvc: string;
  name: string;
}

interface CardPaymentFormProps {
  /** Called with the card details when the form is valid, null otherwise. */
  onChange: (card: CardDetails | null) => void;
  disabled?: boolean;
}

type CardBrand = 'visa' | 'mastercard' | 'amex' | 'unknown';

/**
 * PANs are 12-19 digits (ISO 7812) but each brand has a fixed length: capping
 * the input at the detected brand's length stops typos past the real card
 * number instead of silently accepting extra digits.
 */
const BRAND_RULES: Record<CardBrand, { maxDigits: number; cvcLength: number; label: string }> = {
  visa: { maxDigits: 16, cvcLength: 3, label: 'Visa' },
  mastercard: { maxDigits: 16, cvcLength: 3, label: 'Mastercard' },
  amex: { maxDigits: 15, cvcLength: 4, label: 'Amex' },
  unknown: { maxDigits: 19, cvcLength: 4, label: '' },
};

function detectCardBrand(digits: string): CardBrand {
  if (/^4/.test(digits)) return 'visa';
  if (/^(5[1-5]|2[2-7])/.test(digits)) return 'mastercard';
  if (/^3[47]/.test(digits)) return 'amex';
  return 'unknown';
}

function luhnValid(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = Number(digits[i]);
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return digits.length >= 12 && sum % 10 === 0;
}

function formatCardNumber(value: string): string {
  const raw = value.replace(/\D/g, '');
  const brand = detectCardBrand(raw);
  const digits = raw.slice(0, BRAND_RULES[brand].maxDigits);

  // Amex prints as 4-6-5; every other brand groups by 4.
  const groups =
    brand === 'amex'
      ? [digits.slice(0, 4), digits.slice(4, 10), digits.slice(10, 15)].filter(Boolean)
      : (digits.match(/.{1,4}/g) ?? []);

  return groups.join(' ');
}

const inputClass =
  'w-full rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-sm text-charcoal-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)] placeholder:text-slate-400 focus:border-lime-400 focus:outline-none focus:ring-1 focus:ring-lime-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-charcoal-800 dark:bg-card dark:text-charcoal-50';

/**
 * Bank card form (NytroPay). Card details are sent once to our API over HTTPS
 * for the charge and are never stored client-side or logged.
 */
export function CardPaymentForm({ onChange, disabled }: CardPaymentFormProps) {
  const { t } = useTranslation('billing');

  const [number, setNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [name, setName] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const digits = number.replace(/\s/g, '');
  const brand = detectCardBrand(digits);
  const brandRules = BRAND_RULES[brand];
  const numberValid = luhnValid(digits);
  // Flag a wrong number as soon as the brand's full length is typed, without
  // waiting for blur; incomplete input only errors after leaving the field.
  const numberError =
    digits.length > 0 && !numberValid && (touched.number === true || digits.length >= brandRules.maxDigits);

  const expiryMatch = /^(0[1-9]|1[0-2])\s*\/\s*(\d{2})$/.exec(expiry);
  const expiryParsed = useMemo(() => {
    if (!expiryMatch) return null;
    const month = Number(expiryMatch[1]);
    const year = 2000 + Number(expiryMatch[2]);
    const now = new Date();
    const expired = year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1);
    return expired ? null : { month, year };
  }, [expiryMatch]);

  const cvcValid = /^\d{3,4}$/.test(cvc);

  function emit(nextNumber: string, nextExpiry: { month: number; year: number } | null, nextCvc: string, nextName: string) {
    const nextDigits = nextNumber.replace(/\s/g, '');
    if (luhnValid(nextDigits) && nextExpiry && /^\d{3,4}$/.test(nextCvc)) {
      onChange({
        number: nextDigits,
        exp_month: nextExpiry.month,
        exp_year: nextExpiry.year,
        cvc: nextCvc,
        name: nextName.trim(),
      });
    } else {
      onChange(null);
    }
  }

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-charcoal-800 dark:bg-card">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
          <CreditCard className="h-3.5 w-3.5 text-lime-600 dark:text-lime-400" />
          {t('card_details')}
        </span>
        <span className="flex items-center gap-1 text-[10px] text-slate-400">
          <Lock className="h-3 w-3" />
          {t('card_secure_hint')}
        </span>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <label htmlFor="card-number" className="block text-xs font-medium text-slate-600 dark:text-slate-300">
            {t('card_number')}
          </label>
          {brandRules.label !== '' && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {brandRules.label}
            </span>
          )}
        </div>
        <input
          id="card-number"
          inputMode="numeric"
          autoComplete="cc-number"
          placeholder="4242 4242 4242 4242"
          disabled={disabled}
          value={number}
          onChange={(e) => {
            const formatted = formatCardNumber(e.target.value);
            setNumber(formatted);
            emit(formatted, expiryParsed, cvc, name);
          }}
          onBlur={() => setTouched((s) => ({ ...s, number: true }))}
          className={cn(inputClass, numberError && 'border-red-400 focus:border-red-400 focus:ring-red-400')}
        />
        {numberError && <p className="mt-1 text-xs text-red-500">{t('card_number_invalid')}</p>}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="card-expiry" className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
            {t('card_expiry')}
          </label>
          <input
            id="card-expiry"
            inputMode="numeric"
            autoComplete="cc-exp"
            placeholder="MM/AA"
            disabled={disabled}
            value={expiry}
            onChange={(e) => {
              let v = e.target.value.replace(/[^\d/]/g, '').slice(0, 5);
              if (/^\d{3,}$/.test(v)) v = `${v.slice(0, 2)}/${v.slice(2)}`;
              setExpiry(v);
              const m = /^(0[1-9]|1[0-2])\s*\/\s*(\d{2})$/.exec(v);
              const parsed = m ? { month: Number(m[1]), year: 2000 + Number(m[2]) } : null;
              emit(number, parsed, cvc, name);
            }}
            onBlur={() => setTouched((s) => ({ ...s, expiry: true }))}
            className={cn(inputClass, touched.expiry && !expiryParsed && expiry.length > 0 && 'border-red-400 focus:border-red-400 focus:ring-red-400')}
          />
          {touched.expiry && !expiryParsed && expiry.length > 0 && (
            <p className="mt-1 text-xs text-red-500">{t('card_expiry_invalid')}</p>
          )}
        </div>
        <div>
          <label htmlFor="card-cvc" className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
            {t('card_cvc')}
          </label>
          <input
            id="card-cvc"
            inputMode="numeric"
            autoComplete="cc-csc"
            placeholder={brand === 'amex' ? '1234' : '123'}
            maxLength={brandRules.cvcLength}
            disabled={disabled}
            value={cvc}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, brandRules.cvcLength);
              setCvc(v);
              emit(number, expiryParsed, v, name);
            }}
            onBlur={() => setTouched((s) => ({ ...s, cvc: true }))}
            className={cn(inputClass, touched.cvc && !cvcValid && cvc.length > 0 && 'border-red-400 focus:border-red-400 focus:ring-red-400')}
          />
        </div>
      </div>

      <div>
        <label htmlFor="card-name" className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
          {t('card_holder')}
        </label>
        <input
          id="card-name"
          autoComplete="cc-name"
          placeholder={t('card_holder_placeholder')}
          disabled={disabled}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            emit(number, expiryParsed, cvc, e.target.value);
          }}
          className={cn(inputClass, 'font-sans')}
        />
      </div>
    </div>
  );
}
